import { useEffect, useState } from "react";
import { Download, ShoppingBag } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { useCart } from "../../context/CartContext";
import { eur } from "../../lib/api";
import { SizePicker } from "./JProductCard";
import { JQRCard } from "./JQRCard";

const Block = ({ title, children }) => (
  <div className="border-t border-[#F2B441]/25 pt-5">
    <p className="font-mono-d text-[11px] tracking-[0.22em] uppercase text-[#F2B441] mb-3">{title}</p>
    {children}
  </div>
);

export const JProductSheet = ({ p, onClose }) => {
  const { add, setOpen } = useCart();
  const [v, setV] = useState(null);
  useEffect(() => {
    setV(p?.variants?.[0] ?? null);
  }, [p]);
  if (!p) return null;
  return (
    <Dialog open={!!p} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="product-sheet" data-lenis-prevent
        className="max-w-5xl w-[calc(100%-1.5rem)] p-0 bg-[#143823] border-[#F2B441]/40 text-[#F6EEDC] max-h-[90vh] overflow-y-auto">
        <div className="grid md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          <div className="relative bg-[#F6EEDC] md:sticky md:top-0 md:self-start">
            <img src={p.image} alt={`Etiqueta ${p.name}`} className="w-full aspect-[5/6] object-cover" />
          </div>
          <div className="p-6 md:p-9 flex flex-col gap-6">
            <div>
              <p className="font-mono-d text-[11px] tracking-[0.22em] uppercase text-[#8CD05A]">{p.category}</p>
              <DialogTitle className="font-label text-5xl font-normal leading-none mt-2 text-label-gold">{p.name}</DialogTitle>
              <DialogDescription className="text-[#C9D6C2] mt-2">{p.subtitle}{p.npk ? ` · NPK ${p.npk}` : ""}</DialogDescription>
            </div>
            <div className="space-y-3 text-sm text-[#E3EADF] leading-relaxed">{(p.long || []).map((t, i) => <p key={i}>{t}</p>)}</div>
            <Block title="Dosificación">
              <ul className="space-y-2 text-sm text-[#E3EADF]">
                {(p.dosing || []).map((t, i) => <li key={i} className="pl-4 relative before:absolute before:left-0 before:top-[0.55em] before:w-1.5 before:h-1.5 before:rounded-full before:bg-[#8CD05A]">{t}</li>)}
              </ul>
            </Block>
            {p.composition?.length > 0 && (
              <Block title="Composición garantizada">
                <dl data-testid="product-sheet-composition" className="grid sm:grid-cols-2 gap-x-8 text-sm">
                  {p.composition.map(([k, val]) => (
                    <div key={k} className="flex justify-between gap-3 border-b border-dotted border-[#F6EEDC]/20 py-1.5">
                      <dt className="text-[#C9D6C2]">{k}</dt><dd className="font-mono-d text-[#F6EEDC]">{val}</dd>
                    </div>
                  ))}
                </dl>
              </Block>
            )}
            {p.notes?.length > 0 && (
              <Block title="Recomendaciones">
                <ul className="space-y-1.5 text-sm text-[#E3EADF]">{p.notes.map((t, i) => <li key={i}>· {t}</li>)}</ul>
              </Block>
            )}
            <Block title="QR de la etiqueta">
              <JQRCard url={`${window.location.origin}/?producto=${p.id}`} title={`${p.name} · ficha y dosis`} testid="product-sheet-qr"
                fileName={`jungle-qr-${p.id}`} caption="Para imprimir en la etiqueta: al escanearlo se abre esta ficha con su tabla de dosis." />
            </Block>
            <Block title="Formato">
              <SizePicker p={p} value={v} onChange={setV} testid="product-sheet-size" />
            </Block>
            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <span className="font-serif-d font-black text-4xl" data-testid="product-sheet-price">{eur(v?.price ?? p.price)}</span>
              <div className="flex flex-wrap gap-2">
                {p.pdf && (
                  <a href={p.pdf} target="_blank" rel="noreferrer" data-testid="product-sheet-pdf"
                    className="inline-flex items-center gap-2 rounded-full border border-[#F6EEDC]/35 px-5 h-12 text-sm hover:border-[#F2B441] hover:text-[#F2B441] transition-colors">
                    <Download className="w-4 h-4" /> Etiqueta PDF
                  </a>
                )}
                <button data-testid="product-sheet-add" onClick={() => { add(p, 1, true, v); onClose(); setOpen(true); }}
                  className="inline-flex items-center gap-2 rounded-full bg-[#F2B441] text-[#0E2A1C] px-6 h-12 text-sm font-bold hover:bg-[#F6EEDC] transition-colors">
                  <ShoppingBag className="w-4 h-4" /> Añadir al carrito
                </button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
